import React, { useEffect, useState } from 'react';
import '../styles/components/fmh-connection-prompt.scss';

const CARET_ICON = '/assets/images/caret-left.svg';
const FMH_ENROLLMENT_STATUS_COMPLETED = 'Completed';
const FMH_ENROLLMENT_STATUS_INVITED = 'Invited';
const FMH_ICON = '/assets/images/followmyhealth.svg';
const RELATIONSHIP_TO_PATIENT_SELF = 'Self';

/**
 * Renders the instruction steps for connecting to FollowMyHealth.
 */
function InstructionSteps({ practiceName, isAuthorized }) {
  return (
    <>
      <br />
      <p>
        <strong>1. Sign in to FollowMyHealth or create a new account</strong>
      </p>
      <p>
        Are you new to FollowMyHealth? Choose “Sign Up and Connect” to create
        your FollowMyHealth username and password. If you already have a
        FollowMyHealth account, choose “Sign in and add this connection” to{' '}
        {isAuthorized
          ? 'add the patient to your existing account.'
          : `connect ${practiceName} to your existing account.`}
      </p>
      <br />
      <p>
        <strong>2. Connect your account to {practiceName}</strong>
      </p>
      <p>
        Follow the on-screen prompts to connect your FollowMyHealth account to{' '}
        {practiceName}. This includes entering the invite code. The invite code
        is {isAuthorized ? `the patient’s date of birth` : 'your date of birth'}{' '}
        in the format MMDDYYYY (ex. July 9, 1985 would be 07091985) unless we
        gave you a different code.
      </p>
    </>
  );
}

const FmhConnectionPrompt = ({
  account,
  onSetSpinner,
  practices,
  onGoBackToAccountSelector,
  shouldDisplayBackButton,
}) => {

  useEffect(() => {
    onSetSpinner(false);
  }, [onSetSpinner]);

  const patientName = account.fullName || ' ';
  const practiceName = account.practiceName || ' ';
  const fmhRedirectLink = account.fmhRedirectLink;
  const enrollmentStatus = account.fmhEnrollmentStatus;

  // FMH status checks
  const isPatientConnectedToFMH =
    enrollmentStatus === FMH_ENROLLMENT_STATUS_COMPLETED;
  const isPatientInvitedToFMH =
    enrollmentStatus === FMH_ENROLLMENT_STATUS_INVITED;
  const isAuthorizedIndividual =
    account.relationshipToUser !== RELATIONSHIP_TO_PATIENT_SELF;

  // Determine button text based on patient status
  let buttonText = '';
  if (isPatientConnectedToFMH) {
    buttonText = 'Sign in to FollowMyHealth';
  } else if (isAuthorizedIndividual && isPatientInvitedToFMH) {
    buttonText = `Connect as an authorized individual for ${patientName}`;
  } else if (isPatientInvitedToFMH) {
    buttonText = `Connect to ${practiceName} with FollowMyHealth`;
  }

  const handleFmhRedirect = () => {
    window.location.assign(fmhRedirectLink);
  };

  return (
    <div className="migrated-to-fmh__container">
      {shouldDisplayBackButton && (
      <div className="migrated-to-fmh__header">        
        <button
          className="migrated-to-fmh__back-button btn--link"
          onClick={() => onGoBackToAccountSelector()}
        >
          <span>
            <img
              src={CARET_ICON}
              alt="Caret Icon Directed Left"
              className="migrated-to-fmh__back-button-icon"
            />
          </span>
          Back to Accounts
        </button>
      </div>
      )}
      <div className="migrated-to-fmh__content">
        <div className="migrated-to-fmh__box">
          <img
            src={FMH_ICON}
            alt="FollowMyHealth Logo"
            className="migrated-to-fmh__logo"
          />
          <h2 className="migrated-to-fmh__header-text">
            {practiceName} is now using FollowMyHealth
          </h2>
          <div className="migrated-to-fmh__box-content-left">
            {/* Patient already connected */}
            {isPatientConnectedToFMH && (
              <p>
                Looks like you already have a FollowMyHealth account connected
                to this practice. FollowMyHealth is your new, improved system
                for accessing your personal health records. Click the button
                below to sign in to your FollowMyHealth account. From there
                you'll be able to view your records and appointments, as well as
                message your providers.
              </p>
            )}

            {/* Patient invited but not connected */}
            {isPatientInvitedToFMH && (
              <div>
                <p>
                  FollowMyHealth is your new, improved system for accessing your
                  personal health records.{' '}
                  {isAuthorizedIndividual
                    ? `Looks like you have not completed your invitation to connect to ${practiceName} as an authorized individual for ${patientName} via FollowMyHealth. Click the button below and follow these steps to complete your authorized individual invite with ${practiceName}:`
                    : `Looks like you haven't connected your FollowMyHealth account to ${practiceName}. Click the button below and complete these steps to connect your account:`}
                </p>
                <InstructionSteps
                  practiceName={practiceName}
                  isAuthorized={isAuthorizedIndividual}
                />
              </div>
            )}

            {/* Patient not invited */}
            {!isPatientInvitedToFMH && !isPatientConnectedToFMH && (
              <p>
                FollowMyHealth is your new, improved system for accessing your
                personal health records. To continue accessing these records,
                please reach out to {practiceName} and ask to be invited to
                FollowMyHealth.
              </p>
            )}
          </div>
          {(isPatientConnectedToFMH || isPatientInvitedToFMH) && (
            <>
              <br />
              <div className="migrated-to-fmh__box-content-left">
                Need help with your FollowMyHealth account?&nbsp;
                <a
                  href="https://help.patientfusion.com/s/article/faqs-for-transitioning-from-patient-fusion-to-followmyhealth"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click here
                </a>
                &nbsp;to see common support topics or contact support.
              </div>
              <hr className="migrated-to-fmh__hr" />
              <button
                className="migrated-to-fmh__sign-in-btn"
                onClick={handleFmhRedirect}
              >
                {buttonText}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FmhConnectionPrompt;
